import type { ExternalGameLink } from "./rosebud-catalog";
import { curatedWebCatalogEntries } from "./curated-web-catalog";
import { CATALOG_CATEGORIES, type CatalogCategoryId, type CatalogEntry } from "../contracts/catalog";
import {
  LAUNCHER_CATEGORY_PICKER_FIXED_SLOTS,
  LAUNCHER_PAGE_SIZE,
  paginateWithLeadingSlot,
  type LauncherSlot,
} from "./launcher-pages";

function activeEntries(entries: readonly CatalogEntry[]): readonly CatalogEntry[] {
  return entries.filter((entry) => entry.status === "active");
}

/** Fixed navigation and all ten owner-supplied category icons. */
export function composeCategoryPickerPage(): readonly LauncherSlot[] {
  if (LAUNCHER_CATEGORY_PICKER_FIXED_SLOTS + CATALOG_CATEGORIES.length !== LAUNCHER_PAGE_SIZE) {
    throw new Error("Category picker must exactly fill one launcher page");
  }
  return [
    { kind: "categories" },
    { kind: "create" },
    ...CATALOG_CATEGORIES.map((category) => ({ kind: "category-option" as const, category })),
  ];
}

export function composeCategoryFilteredPages(
  entries: readonly CatalogEntry[],
  category: CatalogCategoryId,
  externalLinks: readonly ExternalGameLink[] = [],
): readonly (readonly LauncherSlot[])[] {
  // Only enrich links already present in this launcher's inventory. Include
  // hidden/excluded URLs in the known set so enrichment cannot resurrect them.
  const knownUrls = new Set(entries.flatMap((entry) =>
    entry.kind === "external" ? [entry.launch.url] : []));
  const visibleUrls = new Set<string>(externalLinks.map((game) => game.url));
  const completeEntries = [...entries];
  for (const entry of curatedWebCatalogEntries()) {
    if (!visibleUrls.has(entry.launch.url) || knownUrls.has(entry.launch.url)) continue;
    knownUrls.add(entry.launch.url);
    completeEntries.push(entry);
  }
  const matches: LauncherSlot[] = activeEntries(completeEntries)
    .filter((entry) => entry.categories.includes(category)
      || (category === "other" && !entry.categories.some((tag) =>
        CATALOG_CATEGORIES.some((known) => known === tag))))
    .map((entry) => ({ kind: "catalog-entry", entry }));
  // Preserve reachability for any already-public link awaiting its adapter.
  if (category === "other") {
    for (const game of externalLinks) {
      if (knownUrls.has(game.url)) continue;
      knownUrls.add(game.url);
      matches.push({ kind: "external", game });
    }
  }
  return paginateWithLeadingSlot({ kind: "categories" }, matches);
}
